import type { MapStyle } from '../MapControlsFAB';
import { ACCRA_CENTER } from '../../../utils/geo';
import { MAPBOX_TOKEN, API_URL, TILES_URL } from '../../../lib/env';
import type { PromotedTileVersions } from '../../../lib/tileVersions';
import { appendTileCacheBust } from '../../../lib/tileVersions';

export { MAPBOX_TOKEN, API_URL, TILES_URL };

// Convert ACCRA_CENTER object to tuple for Mapbox
export const ACCRA_CENTER_TUPLE: [number, number] = [ACCRA_CENTER.lng, ACCRA_CENTER.lat];

/**
 * Max map zoom — matches ghana-* vector sources (style source/layer maxzoom 22).
 * Mapbox overzooms the z22 tile; there is no z24 tile data in our stack.
 */
export const MAP_MAX_ZOOM = 22;
/** Recommended zoom for precise draw/trace anchoring (~0.02 m/px at mid-latitudes). */
export const SURVEY_MIN_ZOOM = 22;
/** Lowest zoom allowed while marking — zoom out for context, then back in to SURVEY_MIN_ZOOM. */
export const MARKING_MIN_ZOOM = 14;
/** Default zoom on first load and after geolocation. */
export const MAP_DEFAULT_ZOOM = 15;
/** Zoom when focusing a searched place or selected transport stop. */
export const MAP_FOCUS_ZOOM = MAP_MAX_ZOOM;

// Map styles - local JSON files with Ghana sources pre-configured
export const MAP_STYLES: Record<MapStyle, string> = {
  standard: '/coordal-standard-style.json',
  dark: '/coordal-dark-style.json',
  satellite: '/mapbox-satellite-style.json',
  outdoors: '/coordal-outdoor-style.json',
  navigation: '/coordal-navigation-style.json',
};

/** Turn-by-turn navigation basemap — matches app light/dark theme. */
export const NAVIGATION_MAP_STYLES = {
  light: '/coordal-navigation-style.json',
  dark: '/coordal-navigation-dark-style.json',
} as const;

export function resolveNavigationStyleUrl(isDark: boolean): string {
  return isDark ? NAVIGATION_MAP_STYLES.dark : NAVIGATION_MAP_STYLES.light;
}

// Layer IDs - every root-level layer in coordal-dark-style.json, grouped for toggle control.
// All layers are at root level (not in Mapbox imports) so they can be toggled via setLayoutProperty.
// Some also use config expressions for visibility (noted with "config:" comments).
export const LAYER_IDS: Record<string, string[]> = {
  // --- Admin boundaries (Ghana custom) ---
  country: [
    'world-ocean-fill',
    'world-bathymetry-depth',
    'world-land-fill',
    'world-countries-outline',
    'world-countries-label',
    'ghana-country-boundary',
  ],
  regions: ['ghana-regions-boundary'],
  districts: ['ghana-districts-boundary'],

  // --- Service areas / postcode grid (hidden by default) ---
  serviceAreas: [
    'ghana-service-areas-fill',
    'ghana-service-areas-line',
    'postcode-service-area-fill',
    'postcode-service-area-outline',
    'postcode-service-area-label',
  ],

  // --- Land & water ---
  landcover: ['landcover'],
  landuse: ['landuse', 'landuse-car-park', 'landuse-car-park-stripes', 'recreation-polygon', 'landuse-education', 'landuse-label'],
  nationalParks: ['national-park'],
  water: ['ghana-water'],
  waterways: ['waterway', 'waterway-shadow', 'ghana-waterways'],
  structures: [
    'land-structure-polygon',
    'land-structure-line',
    'gate-fence-hedge-shade',
    'fence-line',
    'gate-fence-hedge',
    'structures-label',
  ],

  // --- Aeroway ---
  aeroway: ['aeroway-polygon', 'aeroway-line'],

  // --- Terrain (hidden by default) ---
  hillshade: ['ghana-hillshade-shadow', 'ghana-hillshade-highlight'],
  contours: ['ghana-contours-minor', 'ghana-contours-minor-label', 'ghana-contours-major', 'ghana-contours-label'],

  // --- Roads (all road-* layers at root) ---
  roads: [
    'road-pedestrian-polygon-fill',
    'road-path-case', 'road-steps-case', 'road-pedestrian-case',         // config:showPedestrianRoads
    'road-footway-case', 'road-footway', 'road-footway-center', // config:showPedestrianRoads
    'road-zebra', 'road-zebra-bars',                                                // config:showPedestrianRoads
    'road-path-trail', 'road-path', 'road-steps', 'road-pedestrian',     // config:showPedestrianRoads
    'golf-hole-line',
    'road-minor-case', 'road-street-case', 'road-minor-link-case',
    'road-secondary-tertiary-case', 'road-primary-case', 'road-major-link-case',
    'road-motorway-trunk-case',
    'turning-feature', 'road-polygon-case',
    'road-carriageway-corridor',
    'road-others-polygon-fill', 'road-secondary-tertiary-polygon-fill',
    'road-primary-polygon-fill', 'road-motorway-trunk-polygon-fill',
    'road-construction', 'road-minor', 'road-minor-link', 'road-major-link',
    'road-street', 'road-street-low', 'road-secondary-tertiary',
    'road-primary', 'road-motorway-trunk',
    'road-oneway-arrow', 'road-oneway-arrow-trunk', 'road-oneway-arrow-motorway',
    'crosswalks',
  ],

  // --- Granular road controls ---
  motorways: [
    'road-carriageway-corridor',
    'road-motorway-trunk', 'road-motorway-trunk-case', 'road-motorway-trunk-polygon-fill',
    'bridge-motorway-trunk', 'bridge-motorway-trunk-case', 'bridge-motorway-trunk-shadow',
    'bridge-motorway-trunk-2', 'bridge-motorway-trunk-2-case', 'bridge-motorway-trunk-2-shadow',
    'tunnel-motorway-trunk', 'tunnel-motorway-trunk-case',
  ],
  primaryRoads: [
    'road-primary', 'road-primary-case', 'road-primary-polygon-fill',
    'bridge-primary', 'bridge-primary-case', 'bridge-primary-shadow',
    'tunnel-primary', 'tunnel-primary-case',
  ],
  secondaryRoads: [
    'road-secondary-tertiary', 'road-secondary-tertiary-case', 'road-secondary-tertiary-polygon-fill',
    'bridge-secondary-tertiary', 'bridge-secondary-tertiary-case', 'bridge-secondary-tertiary-shadow',
    'tunnel-secondary-tertiary', 'tunnel-secondary-tertiary-case',
  ],
  minorRoads: [
    'road-minor', 'road-minor-case', 'road-minor-link', 'road-minor-link-case',
    'road-street', 'road-street-case', 'road-street-low',
    'bridge-minor', 'bridge-minor-case', 'bridge-minor-shadow',
    'bridge-minor-link', 'bridge-minor-link-case', 'bridge-minor-link-shadow',
    'bridge-street', 'bridge-street-case', 'bridge-street-shadow', 'bridge-street-low',
    'bridge-major-link', 'bridge-major-link-case', 'bridge-major-link-shadow',
    'bridge-major-link-2', 'bridge-major-link-2-case', 'bridge-major-link-2-shadow',
    'tunnel-minor', 'tunnel-minor-case', 'tunnel-minor-link', 'tunnel-minor-link-case',
    'tunnel-street', 'tunnel-street-case', 'tunnel-street-low',
    'tunnel-major-link', 'tunnel-major-link-case',
    'road-major-link', 'road-major-link-case',
    'road-construction', 'bridge-construction', 'tunnel-construction',
  ],
  pedestrianPaths: [
    'road-footway-fill', 'road-footway', 'road-footway-case', 'road-footway-center',
    'road-cycleway-derived-band', 'road-cycleway-case', 'road-cycleway-center',
    'road-zebra', 'road-zebra-bars',
    'road-pedestrian', 'road-pedestrian-case', 'road-path', 'road-path-case',
    'road-path-trail', 'road-steps', 'road-steps-case', 'road-pedestrian-polygon-fill',
    'bridge-pedestrian', 'bridge-pedestrian-case', 'bridge-path', 'bridge-path-case',
    'bridge-path-trail', 'bridge-steps', 'bridge-steps-case',
    'bridge-path-cycleway-piste',
    'tunnel-pedestrian', 'tunnel-pedestrian-case', 'tunnel-path', 'tunnel-path-case',
    'tunnel-path-trail', 'tunnel-steps', 'tunnel-steps-case',
    'tunnel-path-cycleway-piste',
  ],

  // --- Tunnels (all tunnel-* layers) ---
  tunnels: [
    'tunnel-path-case', 'tunnel-pedestrian-case', 'tunnel-steps-case', 'tunnel-minor-case',
    'tunnel-street-case', 'tunnel-minor-link-case', 'tunnel-secondary-tertiary-case',
    'tunnel-primary-case', 'tunnel-major-link-case', 'tunnel-motorway-trunk-case',
    'tunnel-path-trail', 'tunnel-path', 'tunnel-steps', 'tunnel-pedestrian',
    'tunnel-construction', 'tunnel-minor', 'tunnel-minor-link', 'tunnel-major-link',
    'tunnel-street', 'tunnel-street-low', 'tunnel-secondary-tertiary',
    'tunnel-primary', 'tunnel-motorway-trunk',
    'tunnel-oneway-arrow', 'tunnel-oneway-arrow-trunk', 'tunnel-oneway-arrow-motorway',
    'tunnel-path-cycleway-piste',
  ],

  // --- Bridges (all bridge-* layers) ---
  bridges: [
    'bridge-path-case', 'bridge-pedestrian-case', 'bridge-steps-case',
    'bridge-path-trail', 'bridge-path', 'bridge-steps', 'bridge-pedestrian',
    'bridge-minor-shadow', 'bridge-minor-case', 'bridge-minor',
    'bridge-street-shadow', 'bridge-street-case', 'bridge-street', 'bridge-street-low',
    'bridge-minor-link-shadow', 'bridge-minor-link-case', 'bridge-minor-link',
    'bridge-secondary-tertiary-shadow', 'bridge-secondary-tertiary-case', 'bridge-secondary-tertiary',
    'bridge-primary-shadow', 'bridge-primary-case', 'bridge-primary',
    'bridge-major-link-shadow', 'bridge-major-link-case', 'bridge-major-link',
    'bridge-major-link-2-shadow', 'bridge-major-link-2-case', 'bridge-major-link-2',
    'bridge-motorway-trunk-shadow', 'bridge-motorway-trunk-case', 'bridge-motorway-trunk',
    'bridge-motorway-trunk-2-shadow', 'bridge-motorway-trunk-2-case', 'bridge-motorway-trunk-2',
    'bridge-construction',
    'bridge-oneway-arrow', 'bridge-oneway-arrow-trunk', 'bridge-oneway-arrow-motorway',
    'bridge-path-cycleway-piste',
    'bridge-rail', 'bridge-rail-tracks',
  ],

  // --- Rail ---
  rail: ['bridge-rail', 'bridge-rail-tracks'],

  // --- Ferry ---
  ferryRoutes: ['ferry', 'ferry-auto', 'ferry-aerialway-label'],

  // --- Aerialway ---
  aerialways: ['aerialway'],

  // --- Buildings ---
  buildings: ['2d-building', '2d-building-outline-ghana'],
  buildings3D: ['3d-building'],
  buildingLabels: ['building-number-labels'],
  buildingEntrance: ['building-entrance', 'block-number-label'],
  buildingUnderground: ['building-underground'],

  // --- Trees (Ghana custom tiles; 3D models need Mapbox show3dObjects — always on in code) ---
  trees: ['ghana-trees-tile', 'ghana-trees-model'],

  // --- Road labels (config:showRoadLabels) ---
  roadLabels: ['ghana-road-labels', 'ghana-road-labels-divided', 'path-footway-label', 'road-exit-shield'],

  // --- Natural labels (config:showPlaceLabels) ---
  naturalLabels: ['waterway-label', 'natural-line-label', 'natural-point-label', 'water-line-label', 'water-point-label', 'ghana-natural-labels'],

  // --- POI labels ---
  poiLabels: ['ghana-poi-labels', 'ghana-landmark-labels', 'golf-hole-label'],

  // --- Ferry / aerialway line labels (Ghana roads tiles) ---
  // ferry-aerialway-label is also listed under ferryRoutes for toggle control.

  // --- Settlement labels ---
  settlementLabels: ['ghana-town-labels', 'ghana-village-labels'],

  // --- Place labels (cities, regions, districts — not settlements) ---
  placeLabels: [
    'ghana-city-labels',
    'ghana-region-labels',
    'ghana-district-labels',
  ],

  // --- Transit stops (vector tile sprite-based layers) ---
  transportStops: ['transit-trotro-stop', 'transit-lorry-park', 'transit-taxi-rank', 'transit-bus-stop', 'transit-rail-station'],

  // --- Aeroway / motorway overlays (Coordal tiles) ---
  airportLabels: ['airport-label'],
  motorwayExits: ['road-exit-shield'],
  trafficSignals: ['traffic-signals-symbol', 'traffic-signals-label', 'structure-furniture-symbol'],

  // --- Road quality overlay (smoothness from ghana-roads tiles) ---
  roadQuality: ['road-quality-overlay'],

  // --- Seasonal flood exposure (dynamic overlay via useFloodExposureLayer) ---
  floodExposure: ['ghana-flood-exposure-fill'],

  // Observed weather uses per-period layer ids (ghana-observed-weather-*-fill-{period});
  // visibility is managed by useObservedWeatherLayers, not toggleLayer.
};

// Layer group → config property mapping.
// When toggling these groups, we ALSO set the config property so that layers using
// config expressions (e.g. ["case", ["config", "showRoadLabels"], "visible", "none"])
// respond correctly. We still call setLayoutProperty on each layer as well.
export const LAYER_CONFIG_MAP: Record<string, string> = {
  placeLabels: 'showPlaceLabels',
  naturalLabels: 'showPlaceLabels',
  poiLabels: 'showPointOfInterestLabels',
  roadLabels: 'showRoadLabels',
  pedestrianPaths: 'showPedestrianRoads',
};

/**
 * Recursively resolve ["config", "propName"] expressions to their default values.
 */
function resolveConfigExpr(value: any, defaults: Record<string, any>): any {
  if (!Array.isArray(value)) return value;
  if (value[0] === 'config' && value.length === 2) {
    const key = value[1];
    return key in defaults ? defaults[key] : value;
  }
  return value.map((item: any) => resolveConfigExpr(item, defaults));
}

/**
 * Resolve all config expressions in a layer's layout, paint, and filter.
 */
function resolveLayerConfig(layer: any, defaults: Record<string, any>): any {
  const resolved: any = { ...layer };
  if (resolved.layout) {
    const layout: any = { ...resolved.layout };
    for (const key of Object.keys(layout)) {
      layout[key] = resolveConfigExpr(layout[key], defaults);
    }
    resolved.layout = layout;
  }
  if (resolved.paint) {
    const paint: any = { ...resolved.paint };
    for (const key of Object.keys(paint)) {
      paint[key] = resolveConfigExpr(paint[key], defaults);
    }
    resolved.paint = paint;
  }
  if (resolved.filter) {
    resolved.filter = resolveConfigExpr(resolved.filter, defaults);
  }
  return resolved;
}

/**
 * Recursively resolve ["config", …] in any style subtree (fog, lights, etc.).
 */
function resolveConfigInObject(obj: any, defaults: Record<string, any>): any {
  if (!Array.isArray(obj) && (obj === null || typeof obj !== 'object')) return obj;
  if (Array.isArray(obj)) {
    if (obj[0] === 'config' && obj.length === 2) {
      const key = obj[1];
      return key in defaults ? defaults[key] : obj;
    }
    return obj.map((item: any) => resolveConfigInObject(item, defaults));
  }
  const resolved: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    resolved[key] = resolveConfigInObject(value, defaults);
  }
  return resolved;
}

/**
 * Transform style JSON:
 * 1. Rewrite tile source URLs (localhost → production)
 * 2. Rewrite sprite URL to current origin
 * 3. Pre-resolve all ["config", …] expressions and remove `schema` key
 *    so Mapbox GL JS v3 does NOT wrap the style in an internal import
 *    (which would move the sprite into import scope, breaking icon loading)
 */
export const transformStyleUrls = (style: any, layerVersions: PromotedTileVersions = {}): any => {
  if (!style || !style.sources) return style;

  const transformedStyle = { ...style, sources: { ...style.sources } };

  // 1. Transform tile source URLs (+ cache-bust query param after promote)
  Object.keys(transformedStyle.sources).forEach(sourceId => {
    const source = transformedStyle.sources[sourceId];
    if (source.tiles && Array.isArray(source.tiles)) {
      const maxzoom =
        sourceId === 'ghana-roads'
          ? MAP_MAX_ZOOM
          : source.maxzoom;
      transformedStyle.sources[sourceId] = {
        ...source,
        maxzoom,
        tiles: source.tiles.map((url: string) => {
          const rewritten = url
            .replace('http://localhost:8000', TILES_URL)
            .replace('http://127.0.0.1:8000', TILES_URL);
          return appendTileCacheBust(rewritten, layerVersions);
        }),
      };
    }
  });

  // Road names use the lightweight road_labels tileset so label fixes only need
  // a road_labels retile+promote — not a full ghana_roads.mbtiles rebuild.
  if (Array.isArray(transformedStyle.layers)) {
    const hasDividedRoadLabels = transformedStyle.layers.some(
      (layer: any) => layer?.id === 'ghana-road-labels-divided',
    );
    const patchedLayers: any[] = [];

    for (const layer of transformedStyle.layers) {
      if (layer?.id !== 'ghana-road-labels' || layer?.type !== 'symbol') {
        patchedLayers.push(layer);
        continue;
      }

      const layout = { ...(layer.layout ?? {}) };
      delete layout['text-allow-overlap'];
      delete layout['text-ignore-placement'];

      const baseFilter = Array.isArray(layer.filter) && layer.filter[0] === 'all'
        ? layer.filter.slice(1).filter(
            (clause: unknown) =>
              JSON.stringify(clause) !== JSON.stringify(['!', ['has', 'parent_road_id']]),
          )
        : [
            ['==', ['geometry-type'], 'LineString'],
            ['has', 'name'],
          ];

      patchedLayers.push({
        ...layer,
        source: 'ghana-road-labels',
        'source-layer': 'road_labels',
        filter: ['all', ...baseFilter, ['!', ['has', 'parent_road_id']]],
        layout,
      });

      if (!hasDividedRoadLabels) {
        const pathRoadType = ['downcase', ['coalesce', ['get', 'type'], '']];
        const kerbsideRoleLabelExclude = [
          '!',
          [
            'any',
            [
              'in',
              pathRoadType,
              ['literal', ['footway', 'path', 'cycleway', 'pedestrian', 'steps', 'bridleway']],
            ],
            [
              'in',
              ['coalesce', ['get', 'side_facility'], ''],
              ['literal', ['shoulder', 'parking_lane', 'sidewalk', 'cycleway']],
            ],
            [
              'all',
              ['has', 'parent_road_id'],
              ['in', pathRoadType, ['literal', ['road', 'service']]],
            ],
          ],
        ];
        patchedLayers.push({
          ...layer,
          id: 'ghana-road-labels-divided',
          source: 'ghana-road-labels',
          'source-layer': 'road_labels',
          maxzoom: 24,
          filter: [
            'all',
            ['==', ['geometry-type'], 'LineString'],
            ['has', 'name'],
            ['has', 'parent_road_id'],
            kerbsideRoleLabelExclude,
          ],
          layout: {
            ...layout,
            'symbol-sort-key': ['to-number', ['coalesce', ['get', 'id'], 0]],
            'text-allow-overlap': true,
            'text-ignore-placement': true,
          },
        });
      }
    }

    transformedStyle.layers = patchedLayers;
  }

  // 2. Transform sprite URL to use current origin (so it works in dev + deployed)
  if (typeof transformedStyle.sprite === 'string') {
    if (transformedStyle.sprite.startsWith('http://localhost:3001/')) {
      const spritePath = transformedStyle.sprite.replace('http://localhost:3001', '');
      transformedStyle.sprite = `${window.location.origin}${spritePath}`;
    } else if (transformedStyle.sprite.startsWith('/')) {
      transformedStyle.sprite = `${window.location.origin}${transformedStyle.sprite}`;
    }
  }

  // 3. Flatten config: resolve ["config", "propName"] expressions using schema defaults,
  //    then remove `schema` so Mapbox GL JS doesn't create an internal import wrapper.
  if (transformedStyle.schema) {
    const defaults: Record<string, any> = {};
    for (const [key, entry] of Object.entries(transformedStyle.schema as Record<string, any>)) {
      if (entry && typeof entry === 'object' && 'default' in entry) {
        defaults[key] = entry.default;
      }
    }

    if (transformedStyle.layers && Array.isArray(transformedStyle.layers)) {
      transformedStyle.layers = transformedStyle.layers.map((layer: any) =>
        resolveLayerConfig(layer, defaults)
      );
    }

    if (transformedStyle.fog) {
      transformedStyle.fog = resolveConfigInObject(transformedStyle.fog, defaults);
    }
    if (transformedStyle.lights) {
      transformedStyle.lights = resolveConfigInObject(transformedStyle.lights, defaults);
    }

    delete transformedStyle.schema;
  }

  return transformedStyle;
};
